/* eslint-disable react/no-array-index-key */

import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import StateProvinceSelectionDasboard from "../../CampaignCreationCustomSlides/StateProvinceSelectionSlide/components/StateProvinceSelectionDasboard";
import api from "../../../api";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { StateProvinceSet } from "../../../types/campaignTypes";
import { errorToaster, successToaster } from "../../../helpers/toasters";
import { getGeoTargetOptions } from "../../../redux/reducers/campaignToCreate/actions";
import { getStateNamesByIds } from "./stateProvinceSetUtils";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

type ModalState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; set: StateProvinceSet }
  | { mode: "delete"; set: StateProvinceSet };

type MenuAnchor = { el: HTMLElement; set: StateProvinceSet } | null;

const PageRoot = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 20,
  padding: "0 0 32px",
});

const Header = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
});

const PrimaryButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.coralHover, transform: "translateY(-1px)", boxShadow: "none" },
  "&.Mui-disabled": { background: theme.palette.brand.coral, color: theme.palette.brand.white, opacity: 0.6 },
}));

const DangerButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.errorRed,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#DC2626", boxShadow: "none" },
  "&.Mui-disabled": { background: theme.palette.brand.errorRed, color: theme.palette.brand.white, opacity: 0.6 },
}));

const SecondaryButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px solid ${theme.palette.brand.gray200}`,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.gray50, boxShadow: "none" },
}));

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const CountCell = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

const TagsWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "10px 16px 12px",
});

const TagGroupLabel = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  color: theme.palette.brand.gray500,
  padding: "4px 0 2px",
  width: "100%",
}));

const StateTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const MoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  color: theme.palette.brand.gray500,
  padding: 4,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    background: theme.palette.brand.gray100,
    color: theme.palette.brand.gray900,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  minWidth: 180,
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const DangerMenuItem = styled(StyledMenuItem)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.errorRed,
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "48px 24px",
  textAlign: "center" as const,
  color: theme.palette.brand.gray500,
  fontSize: 14,
}));

const LoadingState = styled("div")({
  padding: "48px 24px",
  display: "flex",
  justifyContent: "center",
});

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "32px 0",
}));

const ModalBody = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  minWidth: 720,
  paddingTop: 8,
  "& > div:last-child": { overflowX: "visible", marginTop: 0 },
});

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

const StateProvinceSetsView: FC = () => {
  const dispatch = useDispatch();
  const { selectedAdvertiser } = useGlobalSelectedAdvertiser();
  const brandgenAdvertiserId = selectedAdvertiser?.brandgenAdvertiserId ?? null;
  const geoTargetOptions = useSelector((s: ReduxApplicationState) => s.campaignToCreate.geoTargetOptions);

  const [sets, setSets] = useState<StateProvinceSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState<ModalState>({ mode: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [formName, setFormName] = useState("");

  const [modalSelectedGeoTargetOptions, setModalSelectedGeoTargetOptions] = useState<string[]>([]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current && !geoTargetOptions) {
      hasFetched.current = true;
      dispatch(getGeoTargetOptions());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSets = useCallback(async () => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    try {
      const { payload } = await api.getStateProvinceSets(brandgenAdvertiserId);
      setSets(Array.isArray(payload) ? payload : []);
    } catch {
      setSets([]);
    } finally {
      setIsLoading(false);
    }
  }, [brandgenAdvertiserId]);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const openCreate = () => {
    setFormName("");
    setModalSelectedGeoTargetOptions([]);
    setModal({ mode: "create" });
  };

  const openEdit = (set: StateProvinceSet) => {
    setFormName(set.name);
    setModalSelectedGeoTargetOptions(set.geoTargetIds.map((id) => String(id)));
    setModal({ mode: "edit", set });
  };

  const closeModal = () => setModal({ mode: "closed" });

  const handleModalSetSelected = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      const next = typeof value === "function" ? value(modalSelectedGeoTargetOptions) : value;
      setModalSelectedGeoTargetOptions(next);
    },
    [modalSelectedGeoTargetOptions],
  );

  const handleSave = async () => {
    if (!formName.trim()) {
      errorToaster("Please enter a name for this set.", 4000);
      return;
    }
    const geoTargetIds = modalSelectedGeoTargetOptions.map((id) => Number(id)).filter((id) => id !== -1);
    if (geoTargetIds.length === 0) {
      errorToaster("Please select at least one State/Province.", 4000);
      return;
    }
    if (!brandgenAdvertiserId) return;
    setIsSaving(true);
    try {
      if (modal.mode === "create") {
        await api.createStateProvinceSet({ name: formName.trim(), brandgenAdvertiserId, geoTargetIds });
        successToaster("State/province set created.");
      } else if (modal.mode === "edit") {
        await api.updateStateProvinceSet(modal.set.id, { name: formName.trim(), geoTargetIds });
        successToaster("State/province set updated.");
      }
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to save. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (modal.mode !== "delete") return;
    setIsSaving(true);
    try {
      await api.deleteStateProvinceSet(modal.set.id);
      successToaster("State/province set deleted.");
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to delete. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isFormOpen = modal.mode === "create" || modal.mode === "edit";

  return (
    <PageRoot>
      <Header>{brandgenAdvertiserId && <PrimaryButton onClick={openCreate}>Create New Set</PrimaryButton>}</Header>

      {!brandgenAdvertiserId ? (
        <AdvertiserPrompt>Please select an advertiser to manage state & province sets.</AdvertiserPrompt>
      ) : isLoading || !geoTargetOptions ? (
        <LoadingState>
          <CircularProgress size={32} />
        </LoadingState>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>No state & province sets yet. Create one to get started.</EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>States & Provinces</Th>
                <Th>Last Modified</Th>
                <Th style={{ width: 120 }} />
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const { us, ca } = getStateNamesByIds(geoTargetOptions, set.geoTargetIds);
                const totalCount = us.length + ca.length;
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(set.id)) next.delete(set.id);
                          else next.add(set.id);
                          return next;
                        })
                      }
                    >
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {totalCount.toLocaleString()}
                        </CountCell>
                      </Td>
                      <Td>{set.updatedAt ? new Date(set.updatedAt).toLocaleDateString() : ""}</Td>
                      <Td>
                        <MoreButton
                          type="button"
                          aria-label={`Actions for ${set.name}`}
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            setMenuAnchor({ el: e.currentTarget, set });
                          }}
                        >
                          <MoreIcon />
                        </MoreButton>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={4}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <TagsWrapper>
                            {us.length > 0 && (
                              <>
                                <TagGroupLabel>United States</TagGroupLabel>
                                {us.map((name, index) => (
                                  <StateTag key={`us-${name}-${index}`}>{name}</StateTag>
                                ))}
                              </>
                            )}
                            {ca.length > 0 && (
                              <>
                                <TagGroupLabel>Canada</TagGroupLabel>
                                {ca.map((name, index) => (
                                  <StateTag key={`ca-${name}-${index}`}>{name}</StateTag>
                                ))}
                              </>
                            )}
                          </TagsWrapper>
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StyledMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) openEdit(menuAnchor.set);
            setMenuAnchor(null);
          }}
        >
          Edit
        </StyledMenuItem>
        <DangerMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) setModal({ mode: "delete", set: menuAnchor.set });
            setMenuAnchor(null);
          }}
        >
          Delete
        </DangerMenuItem>
      </Menu>

      <Dialog open={isFormOpen} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{modal.mode === "create" ? "Create State/Province Set" : "Edit State/Province Set"}</DialogTitle>
        <DialogContent>
          <ModalBody>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Set name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            {geoTargetOptions && (
              <StateProvinceSelectionDasboard
                geoTargetOptions={geoTargetOptions}
                selectedGeoTargetOptions={modalSelectedGeoTargetOptions}
                setSelectedGeoTargetOptions={handleModalSetSelected}
              />
            )}
          </ModalBody>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <SecondaryButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={modal.mode === "delete"} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Delete State/Province Set</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14 }}>
            Are you sure you want to delete <strong>{modal.mode === "delete" ? modal.set.name : ""}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <SecondaryButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <DangerButton onClick={handleDelete} disabled={isSaving}>
            {isSaving ? "Deleting..." : "Delete"}
          </DangerButton>
        </DialogActions>
      </Dialog>
    </PageRoot>
  );
};

export default StateProvinceSetsView;
